import { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { formatDualCurrency } from '../utils/currency';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GlobalStructureSection } from '../components/GlobalStructureSection';
import { ListingsManagement } from '../components/ListingsManagement';
import { SocialLinksManagement } from '../components/SocialLinksManagement';
import { SearchFiltersManagement } from '../components/SearchFiltersManagement';
import { EmailNotificationsManagement } from '../components/EmailNotificationsManagement';
import { PagesManagement } from '../components/PagesManagement';
import { ListingLifecycleManagement } from '../components/ListingLifecycleManagement';
import { RolesManagement } from '../components/RolesManagement';
import { LocationsManagement } from '../components/LocationsManagement';
import { AppearanceManagement } from '../components/AppearanceManagement';
import { LanguagesManagement } from '../components/LanguagesManagement';
import { BackupsSection } from '../components/BackupsSection';
import { LayoutManagement } from '../components/LayoutManagement';
import { AdminPagination } from '../components/admin/AdminPagination';
import { MaintenanceBanner } from '../components/admin/MaintenanceBanner';
import { Switch } from '../components/ui/switch';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PLATFORM_TZ, getStandardTimezones, setCachedPlatformTimezone } from '../utils/platformTimezone';
import { formatDistanceToNow } from 'date-fns';
import {
  Star, CheckCircle, XCircle, Trash, ShieldCheck, UserCircle, Lock,
  ChartBar, Users, ListDashes, ChatCircle, ClockCounterClockwise, Clock, Gear, Rocket, Database, DownloadSimple, UploadSimple, Plus, PencilSimple, ArrowUp, ArrowDown, Globe, Flag, List, MagnifyingGlass, Palette, FileText, EnvelopeSimple, MapPin, Wrench, CaretDown, CloudArrowUp, SquaresFour, WarningCircle
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each section maps to ONE OR MORE permission keys. The section is shown if
// the user has ANY of the listed permissions. This lets a "Support Agent"
// with just `view_users` see the Users tab and nothing else.
const NAV_GROUPS = [
  {
    label: 'Core',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: ChartBar, perms: ['view_admin_dashboard', 'access_admin_panel'] },
      { key: 'users', label: 'Users', icon: Users, perms: ['view_users'] },
      { key: 'listings', label: 'Listings', icon: ListDashes, perms: ['view_all_listings'] },
      { key: 'reviews', label: 'Reviews', icon: Star, perms: ['view_reviews'] },
      { key: 'reports', label: 'Reports', icon: Flag, perms: ['view_all_listings', 'view_reviews'] },
    ],
  },
  {
    label: 'Structure',
    items: [
      { key: 'structure', label: 'Global Structure', icon: Globe, perms: ['manage_categories'] },
      { key: 'search-filters', label: 'Search Filters', icon: MagnifyingGlass, perms: ['manage_categories'] },
      { key: 'locations', label: 'Locations', icon: MapPin, perms: ['manage_locations'] },
    ],
  },
  {
    label: 'Content & UI',
    items: [
      { key: 'appearance', label: 'Appearance', icon: Palette, perms: ['manage_platform_config'] },
      { key: 'languages', label: 'Languages', icon: Globe, perms: ['manage_platform_config'] },
      { key: 'pages', label: 'Pages', icon: FileText, perms: ['manage_email_templates', 'manage_platform_config'] },
      { key: 'layout', label: 'Layout', icon: SquaresFour, perms: ['manage_platform_config'] },
    ],
  },
  {
    label: 'Communication',
    items: [
      { key: 'emails', label: 'Email & Notifications', icon: EnvelopeSimple, perms: ['manage_email_settings', 'manage_email_templates'] },
    ],
  },
  {
    label: 'System',
    items: [
      { key: 'audit', label: 'Audit Log', icon: ClockCounterClockwise, perms: ['view_audit_log'] },
      { key: 'lifecycle', label: 'Listing Lifecycle', icon: Clock, perms: ['manage_platform_config'] },
      { key: 'roles', label: 'Roles & Permissions', icon: ShieldCheck, perms: ['manage_roles_permissions'] },
      { key: 'settings', label: 'Platform Settings', icon: Gear, perms: ['manage_platform_config'] },
      { key: 'database', label: 'Database', icon: Database, perms: ['manage_platform_config'] },
      // iter 82: Backups — super-admin only. `role:admin` gate is enforced on every
      // backend endpoint. We also gate visibility in the sidebar via the `roleOnly`
      // marker below so admins with permission-based roles don't see a broken link.
      { key: 'backups', label: 'Backups', icon: CloudArrowUp, roleOnly: 'admin' },
    ],
  },
  {
    label: 'Extra',
    items: [
      { key: 'promotions', label: 'Promotions', icon: Rocket, perms: ['manage_platform_config'] },
    ],
  },
];

const ADMIN_ALLOWED_PAGE_SIZES = [10, 25, 50];
const _readPageSize = (key) => {
  const v = Number(localStorage.getItem(key));
  return ADMIN_ALLOWED_PAGE_SIZES.includes(v) ? v : 10;
};

export const AdminPage = () => {
  const { t } = useTranslation();
  const { user, isAdmin, hasAnyPermission, permissions, loading: authLoading } = useAuth();
  const userRole = user?.role || 'user';
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [listingPage, setListingPage] = useState(1);
  const [listingPageSize, setListingPageSize] = useState(() => _readPageSize('admin.listings.pageSize'));
  const [listingPagination, setListingPagination] = useState({ total: 0, page: 1, page_size: 10, total_pages: 0 });
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('pending');
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewPageSize, setReviewPageSize] = useState(() => _readPageSize('admin.reviews.pageSize'));
  const [reviewPagination, setReviewPagination] = useState({ total: 0, page: 1, page_size: 10, total_pages: 0 });
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('active');
  const [userIncludeDeleted, setUserIncludeDeleted] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [userSort, setUserSort] = useState('default');
  const [userPageSize, setUserPageSize] = useState(() => _readPageSize('admin.users.pageSize'));
  const [userPagination, setUserPagination] = useState({ total: 0, page: 1, page_size: 10, total_pages: 0 });
  const ADMIN_PAGE_SIZE_OPTIONS = [10, 25, 50];
  const USER_PAGE_SIZE_OPTIONS = ADMIN_PAGE_SIZE_OPTIONS;
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(() => _readPageSize('admin.audit.pageSize'));
  const [auditPagination, setAuditPagination] = useState({ total: 0, page: 1, page_size: 10, total_pages: 0 });
  const [auditCategory, setAuditCategory] = useState('all');
  const [auditActorSearch, setAuditActorSearch] = useState('');
  // Debounced/committed actor search — only fires fetch on submit/blur.
  const [auditActorSearchCommitted, setAuditActorSearchCommitted] = useState('');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ platform_facebook_url: '', platform_youtube_url: '', platform_whatsapp_number: '' });
  const [promotions, setPromotions] = useState([]);
  const [promoFilter, setPromoFilter] = useState('pending');
  const [exportStats, setExportStats] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportFilter, setReportFilter] = useState('pending');
  const [reportPage, setReportPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(() => _readPageSize('admin.reports.pageSize'));
  const [reportPagination, setReportPagination] = useState({ total: 0, page: 1, page_size: 10, total_pages: 0 });

  // Persist rows-per-page selections across reloads.
  useEffect(() => { localStorage.setItem('admin.users.pageSize', String(userPageSize)); }, [userPageSize]);
  useEffect(() => { localStorage.setItem('admin.listings.pageSize', String(listingPageSize)); }, [listingPageSize]);
  useEffect(() => { localStorage.setItem('admin.reviews.pageSize', String(reviewPageSize)); }, [reviewPageSize]);
  useEffect(() => { localStorage.setItem('admin.reports.pageSize', String(reportPageSize)); }, [reportPageSize]);
  useEffect(() => { localStorage.setItem('admin.audit.pageSize', String(auditPageSize)); }, [auditPageSize]);

  // Filter the nav by what the user can actually access. A section appears
  // when the user has ANY of its required permissions. Iter 82: entries
  // that carry a `roleOnly` marker are gated strictly on `user.role` —
  // permission-based bypass does NOT apply (used by the Backups panel).
  const visibleGroups = NAV_GROUPS
    .map(g => ({
      ...g,
      items: g.items.filter(n => {
        if (n.roleOnly) return userRole === n.roleOnly;
        return hasAnyPermission(n.perms);
      }),
    }))
    .filter(g => g.items.length > 0);

  // If the URL is pointing at a section the user can't access, fall back to
  // the first visible section. This also gracefully handles role downgrades
  // mid-session.
  useEffect(() => {
    if (authLoading) return;
    const allVisibleKeys = visibleGroups.flatMap(g => g.items.map(i => i.key));
    if (allVisibleKeys.length > 0 && !allVisibleKeys.includes(activeSection)) {
      setActiveSection(allVisibleKeys[0]);
    }
  }, [authLoading, permissions, activeSection, visibleGroups]);

  // Frontend route guard. Backend already enforces, but this stops the
  // unauthorized user from seeing a flicker of the admin shell.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!hasAnyPermission(['access_admin_panel', 'view_admin_dashboard'])) {
      navigate('/', { replace: true });
    }
  }, [authLoading, user, permissions, hasAnyPermission, navigate]);

  useEffect(() => {
    if (activeSection === 'dashboard') fetchStats();
    else if (activeSection === 'listings') fetchAllListings();
    else if (activeSection === 'reviews') fetchReviews();
    else if (activeSection === 'users') fetchUsers();
    else if (activeSection === 'audit') fetchAuditLog();
    else if (activeSection === 'promotions') fetchPromotions();
    else if (activeSection === 'reports') fetchReports();
    else if (activeSection === 'settings') fetchSettings();
    else if (activeSection === 'database') fetchExportStats();
    // 'fields' section manages its own data fetching
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, reviewFilter, promoFilter, reportFilter, userStatusFilter, userIncludeDeleted, userPage, userSort, userPageSize, reviewPage, reviewPageSize, reportPage, reportPageSize, auditPage, auditPageSize, auditCategory, auditActorSearchCommitted]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch { toast.error('Failed to load stats'); }
    finally { setLoading(false); }
  };

  const fetchAllListings = () => {}; // Managed by ListingsManagement component

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', reviewFilter);
      params.set('page', String(reviewPage));
      params.set('page_size', String(reviewPageSize));
      const { data } = await axios.get(`${API}/admin/reviews?${params.toString()}`, { withCredentials: true });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setReviews(items);
      if (!Array.isArray(data) && data) {
        setReviewPagination({
          total: data.total || 0,
          page: data.page || 1,
          page_size: data.page_size || reviewPageSize,
          total_pages: data.total_pages || 0,
        });
      }
    } catch { toast.error('Failed to load reviews'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userSearch) params.set('search', userSearch);
      if (userStatusFilter && userStatusFilter !== 'all') params.set('status', userStatusFilter);
      if (userIncludeDeleted) params.set('include_deleted', 'true');
      params.set('page', String(userPage));
      params.set('page_size', String(userPageSize));
      if (userSort && userSort !== 'default') params.set('sort', userSort);
      const qs = params.toString();
      const url = `${API}/admin/users${qs ? `?${qs}` : ''}`;
      const { data } = await axios.get(url, { withCredentials: true });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setUsers(items);
      if (!Array.isArray(data) && data) {
        setUserPagination({
          total: data.total || 0,
          page: data.page || 1,
          page_size: data.page_size || userPageSize,
          total_pages: data.total_pages || 0,
        });
      }
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const fetchAuditLog = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(auditPage));
      params.set('page_size', String(auditPageSize));
      // Map UI category → backend `action` regex (existing field, regex-matched server-side).
      const categoryToRegex = {
        user: '^user_',
        listing: '^listing_',
        report: '^report_',
      };
      if (auditCategory && auditCategory !== 'all' && categoryToRegex[auditCategory]) {
        params.set('action', categoryToRegex[auditCategory]);
      }
      if (auditActorSearchCommitted) params.set('actor_search', auditActorSearchCommitted);
      const { data } = await axios.get(`${API}/admin/audit-log?${params.toString()}`, { withCredentials: true });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setAuditLogs(items);
      if (!Array.isArray(data) && data) {
        setAuditPagination({
          total: data.total || 0,
          page: data.page || 1,
          page_size: data.page_size || auditPageSize,
          total_pages: data.total_pages || 0,
        });
      }
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { withCredentials: true });
      setSettings(data);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const handleSaveSettings = async (newSettings) => {
    try {
      await axios.put(`${API}/admin/settings`, newSettings, { withCredentials: true });
      toast.success('Settings saved');
      setSettings(newSettings);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
      throw err;
    }
  };

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promotions?status=${promoFilter}`, { withCredentials: true });
      setPromotions(data);
    } catch { toast.error('Failed to load promotions'); }
    finally { setLoading(false); }
  };

  const handlePromoAction = async (promoId, action) => {
    try {
      await axios.put(`${API}/admin/promotions/${promoId}?action=${action}`, {}, { withCredentials: true });
      toast.success(`Promotion ${action}d`);
      fetchPromotions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('status', reportFilter);
      params.set('page', String(reportPage));
      params.set('page_size', String(reportPageSize));
      const { data } = await axios.get(`${API}/admin/reports?${params.toString()}`, { withCredentials: true });
      const items = Array.isArray(data) ? data : (data?.items || []);
      setReports(items);
      if (!Array.isArray(data) && data) {
        setReportPagination({
          total: data.total || 0,
          page: data.page || 1,
          page_size: data.page_size || reportPageSize,
          total_pages: data.total_pages || 0,
        });
      }
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  };

  const handleReportAction = async (reportId, action) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}/resolve?action=${action}`, {}, { withCredentials: true });
      toast.success(action === 'dismiss' ? 'Report dismissed' : 'Listing removed');
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const fetchExportStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/export/stats`, { withCredentials: true });
      setExportStats(data);
    } catch { toast.error('Failed to load export stats'); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.setAttribute('download', `ato_export_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Database exported successfully');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      JSON.parse(text); // validate JSON
      await axios.post(`${API}/admin/import`, text, { withCredentials: true, headers: { 'Content-Type': 'application/json' }, timeout: 120000 });
      toast.success('Database imported successfully');
      fetchExportStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed — invalid JSON file');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleModerate = async (listingId, status) => {
    try {
      await axios.put(`${API}/admin/listings/${listingId}/moderate?status=${status}`, {}, { withCredentials: true });
      toast.success(`Listing ${status}`);
      fetchAllListings();
    } catch { toast.error('Failed'); }
  };

  const handleReviewModerate = async (reviewId, status) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/moderate?status=${status}`, {}, { withCredentials: true });
      toast.success(`Review ${status}`);
      fetchReviews();
    } catch { toast.error('Failed'); }
  };

  const handleReviewDelete = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted');
      fetchReviews();
    } catch { toast.error('Failed'); }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      // New RBAC endpoint accepts any role_key (default + custom).
      await axios.put(
        `${API}/admin/users/${userId}/role`,
        { role_key: role },
        { withCredentials: true }
      );
      toast.success(`Role changed to ${role}`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSuspend = async (userId, suspended) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/suspend?suspended=${suspended}`, {}, { withCredentials: true });
      toast.success(suspended ? 'User suspended' : 'User reactivated');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleResetPassword = async (userId) => {
    try {
      const { data } = await axios.post(`${API}/admin/users/${userId}/reset-password`, {}, { withCredentials: true });
      toast.success(`Password reset. Temp: ${data.temp_password}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleFlag = async (userId, reason) => {
    if (!window.confirm(`Mark this user as ${reason}? This is a destructive action.`)) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/flag?reason=${encodeURIComponent(reason)}`, {}, { withCredentials: true });
      toast.success(`User flagged as ${reason}`);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to flag user'); }
  };

  const handleUnflag = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unflag`, {}, { withCredentials: true });
      toast.success('Flag cleared');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to unflag'); }
  };

  const handleClearRisk = async (userId) => {
    if (!window.confirm('Clear risk score and restore any auto-hidden listings for this user?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/clear-risk`, {}, { withCredentials: true });
      toast.success('Risk cleared; listings restored');
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clear risk'); }
  };

  // ─── Signup blocklist (anti re-registration of flagged users) ──────
  const [blocklist, setBlocklist] = useState([]);
  const [blocklistOpen, setBlocklistOpen] = useState(false);
  const fetchBlocklist = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/blocklist`, { withCredentials: true });
      setBlocklist(data.entries || []);
    } catch (err) { /* silent */ void err; }
  };
  useEffect(() => {
    if (activeSection === 'users') fetchBlocklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);
  const handleBlocklistRemove = async (userId) => {
    try {
      await axios.delete(`${API}/admin/blocklist/${userId}`, { withCredentials: true });
      toast.success('Blocklist entry removed');
      fetchBlocklist();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, i) => (
      <Star key={i} size={14} weight={i < rating ? 'fill' : 'regular'} className={i < rating ? 'text-accent' : 'text-text_muted'} />
    ));

  return (
    <div className="min-h-screen bg-surface">
      <Header />
      <MaintenanceBanner />
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-6" data-testid="admin-title">Admin Panel</h1>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar — grouped */}
          <aside className="md:w-60 flex-shrink-0" data-testid="admin-sidebar">
            <nav className="bg-white border border-border rounded-lg overflow-hidden">
              {visibleGroups.map((grp, gi) => (
                <div key={grp.label} data-testid={`nav-group-${grp.label.toLowerCase().replace(/\s|&/g, '-')}`}>
                  <p className={`px-4 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-text_muted bg-surface/50 ${gi > 0 ? 'border-t border-border' : ''}`}>
                    {grp.label}
                  </p>
                  {grp.items.map(item => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.key;
                    return (
                      <button
                        key={item.key}
                        onClick={() => setActiveSection(item.key)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                          isActive ? 'bg-primary text-white' : 'text-text_muted hover:bg-surface hover:text-text_main'
                        }`}
                        data-testid={`nav-${item.key}`}
                      >
                        <Icon size={18} />
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.placeholder && (
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isActive ? 'bg-white/25 text-white' : 'bg-warning-soft text-warning'}`}>
                            Soon
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              </div>
            ) : activeSection === 'dashboard' ? (
              <DashboardSection stats={stats} />
            ) : activeSection === 'listings' ? (
              <ListingsManagement />
            ) : activeSection === 'reviews' ? (
              <ReviewsSection
                reviews={reviews}
                filter={reviewFilter}
                onFilterChange={(v) => { setReviewPage(1); setReviewFilter(v); }}
                onModerate={handleReviewModerate}
                onDelete={handleReviewDelete}
                renderStars={renderStars}
                pagination={reviewPagination}
                page={reviewPage}
                onPageChange={setReviewPage}
                pageSize={reviewPageSize}
                onPageSizeChange={(v) => { setReviewPage(1); setReviewPageSize(v); }}
                pageSizeOptions={ADMIN_PAGE_SIZE_OPTIONS}
              />
            ) : activeSection === 'users' ? (
              <UsersSection
                users={users}
                availableRoles={availableRoles}
                search={userSearch}
                onSearchChange={setUserSearch}
                onSearch={() => { setUserPage(1); fetchUsers(); }}
                statusFilter={userStatusFilter}
                onStatusFilterChange={(v) => { setUserPage(1); setUserStatusFilter(v); }}
                includeDeleted={userIncludeDeleted}
                onIncludeDeletedChange={(v) => { setUserPage(1); setUserIncludeDeleted(v); }}
                sort={userSort}
                onSortChange={(v) => { setUserPage(1); setUserSort(v); }}
                page={userPage}
                onPageChange={setUserPage}
                pageSize={userPageSize}
                onPageSizeChange={(v) => { setUserPage(1); setUserPageSize(v); }}
                pageSizeOptions={USER_PAGE_SIZE_OPTIONS}
                pagination={userPagination}
                onChangeRole={handleChangeRole}
                onSuspend={handleSuspend}
                onDelete={handleDeleteUser}
                onResetPassword={handleResetPassword}
                onFlag={handleFlag}
                onUnflag={handleUnflag}
                onClearRisk={handleClearRisk}
                t={t}
              />
            ) : activeSection === 'audit' ? (
              <AuditSection
                logs={auditLogs}
                pagination={auditPagination}
                page={auditPage}
                onPageChange={setAuditPage}
                pageSize={auditPageSize}
                onPageSizeChange={(v) => { setAuditPage(1); setAuditPageSize(v); }}
                pageSizeOptions={ADMIN_PAGE_SIZE_OPTIONS}
                category={auditCategory}
                onCategoryChange={(v) => { setAuditPage(1); setAuditCategory(v); }}
                actorSearch={auditActorSearch}
                onActorSearchChange={setAuditActorSearch}
                onActorSearchSubmit={() => { setAuditPage(1); setAuditActorSearchCommitted(auditActorSearch.trim()); }}
                onActorSearchClear={() => { setAuditActorSearch(''); setAuditActorSearchCommitted(''); setAuditPage(1); }}
              />
            ) : activeSection === 'promotions' ? (
              <PromotionsSection promotions={promotions} filter={promoFilter} setFilter={setPromoFilter} onAction={handlePromoAction} />
            ) : activeSection === 'reports' ? (
              <ReportsSection
                reports={reports}
                filter={reportFilter}
                setFilter={(v) => { setReportPage(1); setReportFilter(v); }}
                onAction={handleReportAction}
                pagination={reportPagination}
                page={reportPage}
                onPageChange={setReportPage}
                pageSize={reportPageSize}
                onPageSizeChange={(v) => { setReportPage(1); setReportPageSize(v); }}
                pageSizeOptions={ADMIN_PAGE_SIZE_OPTIONS}
              />
            ) : activeSection === 'settings' ? (
              <SettingsSection />
            ) : activeSection === 'database' ? (
              <DatabaseSection stats={exportStats} onExport={handleExport} onImport={handleImport} exporting={exporting} importing={importing} />
            ) : activeSection === 'structure' ? (
              <GlobalStructureSection renderFieldsTab={() => <FieldManagementSection />} />
            ) : activeSection === 'layout' ? (
              <LayoutManagement />
            ) : activeSection === 'search-filters' ? (
              <SearchFiltersManagement />
            ) : activeSection === 'locations' ? (
              <LocationsManagement />
            ) : activeSection === 'appearance' ? (
              <AppearanceManagement />
            ) : activeSection === 'pages' ? (
              <PagesManagement />
            ) : activeSection === 'lifecycle' ? (
              <ListingLifecycleManagement />
            ) : activeSection === 'roles' ? (
              <RolesManagement />
            ) : activeSection === 'emails' ? (
              <EmailNotificationsManagement />
            ) : activeSection === 'languages' ? (
              <LanguagesManagement />
            ) : activeSection === 'backups' ? (
              <BackupsSection />
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
};

/* ─── Placeholder Section (UI-only for future modules) ─── */
const PlaceholderSection = ({ title, description }) => (
  <div data-testid={`placeholder-${title.toLowerCase().replace(/\s|&/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <h2 className="text-xl font-bold">{title}</h2>
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-warning-soft text-warning">
        Coming soon
      </span>
    </div>
    <p className="text-sm text-text_muted mb-6">{description}</p>

    <div className="bg-white border border-dashed border-border rounded-lg p-10 text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-warning-soft border border-warning mb-4">
        <Wrench size={24} className="text-warning" />
      </div>
      <h3 className="text-base font-semibold text-text_strong mb-2">Module not yet active</h3>
      <p className="text-sm text-text_muted max-w-md mx-auto">
        This module is not yet active. It will be implemented in a future update.
      </p>
    </div>
  </div>
);

/* ─── Dashboard ─── */
const StatCard = ({ label, value, sub, color = 'text-primary', tone = 'neutral', testId }) => {
  // Tone gives us a subtle background/border for "needs attention" KPIs
  // without breaking visual consistency with the rest of the cards.
  const TONE_CLASSES = {
    neutral: 'bg-white border-border',
    warning: 'bg-warning-soft border-warning',
    danger: 'bg-danger-soft border-danger',
  };
  const slug = testId || `stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
  return (
    <div className={`border rounded-lg p-5 ${TONE_CLASSES[tone] || TONE_CLASSES.neutral}`} data-testid={slug}>
      <p className="text-sm text-text_muted font-medium mb-1">{label}</p>
      {/* 24 px desktop / 20 px mobile. Long string values like "Antananarivo" or
          "vehicles" used to dominate the card at text-3xl (30 px); this size
          keeps the visual weight of numeric KPIs like "34" while letting text
          values breathe. */}
      <p className={`text-xl md:text-2xl font-bold tracking-tight break-words ${color}`}>{value}</p>
      {sub && <p className="text-xs text-text_muted mt-1">{sub}</p>}
    </div>
  );
};

// Compute % delta vs previous period; null if previous is 0 (avoid /0).
const pctDelta = (current, previous) => {
  if (previous === 0 || previous == null) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const formatDelta = (current, previous) => {
  const pct = pctDelta(current, previous);
  if (pct === null) return current > 0 ? `+${current} new` : null;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct}% vs prev`;
};

const SectionHeader = ({ title, subtitle }) => (
  // Supportive label, not a headline. Smaller/medium weight, muted color,
  // tighter tracking. Generous bottom spacing keeps the eye on the KPI values.
  <div className="mb-5 mt-6 first:mt-0">
    <h3 className="text-[11px] font-medium text-text_muted uppercase tracking-[0.08em]">{title}</h3>
    {subtitle && <p className="text-xs text-text_muted/80 mt-1">{subtitle}</p>}
  </div>
);

// ─── Collapsible dashboard section ─────────────────────────────────
// Wraps the existing SectionHeader + content. Each section's open state is
// keyed by `id` and persisted in `localStorage('dashboard_section_state')`
// so every admin gets a personalized layout that survives reloads.
const DASHBOARD_STATE_KEY = 'dashboard_section_state';

const readSectionState = () => {
  try {
    return JSON.parse(localStorage.getItem(DASHBOARD_STATE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeSectionState = (state) => {
  try {
    localStorage.setItem(DASHBOARD_STATE_KEY, JSON.stringify(state));
  } catch { /* ignore quota / privacy mode */ }
};

const CollapsibleSection = ({ id, title, subtitle, defaultOpen = true, testId, children }) => {
  // Hydrate from localStorage; fall back to `defaultOpen` for sections the
  // admin has never interacted with. We compute the initial value lazily so
  // SSR/first-render doesn't read storage twice.
  const [open, setOpen] = useState(() => {
    const s = readSectionState();
    return id in s ? s[id] === 'expanded' : defaultOpen;
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    const s = readSectionState();
    s[id] = next ? 'expanded' : 'collapsed';
    writeSectionState(s);
  };

  return (
    <section data-testid={testId} data-section-id={id}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`section-body-${id}`}
        className="w-full flex items-center justify-between gap-3 mt-6 mb-4 first:mt-0 group focus:outline-none"
        data-testid={`${testId}-toggle`}
      >
        <div className="text-left">
          <h3 className="text-[11px] font-medium text-text_muted uppercase tracking-[0.08em] group-hover:text-text_main transition-colors">
            {title}
          </h3>
          {subtitle && <p className="text-xs text-text_muted/80 mt-1">{subtitle}</p>}
        </div>
        <CaretDown
          size={16}
          weight="bold"
          className={`text-text_muted flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-0' : '-rotate-90'}`}
          aria-hidden="true"
        />
      </button>
      {/* Animated reveal — `grid-rows-[0fr|1fr]` lets us animate height from
          intrinsic content size without measuring it manually. */}
      <div
        id={`section-body-${id}`}
        className={`grid transition-[grid-template-rows] duration-200 ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          {/* mb-2 inside so collapsed section doesn't leave a stale gap */}
          <div className="pb-2">{children}</div>
        </div>
      </div>
    </section>
  );
};

const DashboardSection = ({ stats }) => {
  if (!stats) return <p className="text-text_muted">No data</p>;

  const { users, listings, reviews, messages, reports, demand } = stats;

  // Compose "Needs Attention" tone — danger if >0, neutral when zero
  // so admins instantly spot what requires action.
  const toneIfPositive = (n) => (n > 0 ? 'warning' : 'neutral');

  return (
    <div data-testid="dashboard-section" className="space-y-6">
      <h2 className="text-xl font-bold">Platform Overview</h2>

      {/* A. Needs Attention */}
      <CollapsibleSection
        id="needs-attention"
        title="Needs Attention"
        subtitle="Items requiring review or moderation"
        defaultOpen={true}
        testId="kpi-needs-attention"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Pending Listings" value={listings.pending || 0}
            sub="Awaiting approval"
            color="text-warning" tone={toneIfPositive(listings.pending)}
            testId="kpi-pending-listings"
          />
          <StatCard
            label="Pending Reports" value={reports?.pending || 0}
            sub="Listing reports to triage"
            color="text-danger" tone={toneIfPositive(reports?.pending)}
            testId="kpi-pending-reports"
          />
          <StatCard
            label="Unread Messages" value={messages.unread || 0}
            sub={`${messages.total} total`}
            color="text-text_main"
            testId="kpi-unread-messages"
          />
          <StatCard
            label="Suspended Users" value={users.suspended || 0}
            sub={users.suspended > 0 ? 'Need review' : 'All clear'}
            color="text-danger" tone={toneIfPositive(users.suspended)}
            testId="kpi-suspended-users"
          />
        </div>
      </CollapsibleSection>

      {/* B. Listings Health */}
      <CollapsibleSection
        id="listings-health"
        title="Listings Health"
        subtitle="Lifecycle states across the platform"
        defaultOpen={true}
        testId="kpi-listings-health"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Pending" value={listings.pending || 0}
            color="text-warning" testId="kpi-listings-pending"
          />
          <StatCard
            label="Rejected" value={listings.rejected || 0}
            color="text-danger" testId="kpi-listings-rejected"
          />
          <StatCard
            label="Expiring Soon" value={listings.expiring_soon || 0}
            sub="Next 48 hours"
            color="text-warning" testId="kpi-listings-expiring-soon"
          />
          <StatCard
            label="Expired" value={listings.expired || 0}
            sub="In grace period"
            color="text-text_main" testId="kpi-listings-expired"
          />
        </div>
      </CollapsibleSection>

      {/* C. User Activity */}
      <CollapsibleSection
        id="user-activity"
        title="User Activity"
        subtitle="Engagement signals"
        defaultOpen={true}
        testId="kpi-user-activity"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="New Users (7d)" value={users.new_7d || 0}
            sub={formatDelta(users.new_7d || 0, users.new_prev_7d || 0) || 'No prior data'}
            color="text-success" testId="kpi-new-users-7d"
          />
          <StatCard
            label="Active Today" value={users.active_today || 0}
            sub={`${users.active} active total`}
            color="text-text_main" testId="kpi-active-today"
          />
          <StatCard
            label="Users with 0 Listings" value={users.with_zero_listings || 0}
            sub="Activation opportunity"
            color="text-text_main" testId="kpi-zero-listings"
          />
          <StatCard
            label="Total Users" value={users.total || 0}
            sub={`${users.admins} admins · ${users.moderators} mods`}
            testId="kpi-total-users"
          />
        </div>
      </CollapsibleSection>

      {/* D. Growth / Trends */}
      <CollapsibleSection
        id="growth"
        title="Growth & Trends"
        subtitle="Daily and weekly deltas"
        defaultOpen={true}
        testId="kpi-growth"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="New Listings Today" value={listings.new_today || 0}
            sub={listings.new_yesterday != null
              ? `Yesterday: ${listings.new_yesterday}`
              : null}
            color="text-success" testId="kpi-new-listings-today"
          />
          <StatCard
            label="Active Listings" value={listings.active || 0}
            sub={`${listings.total} total`}
            color="text-primary" testId="kpi-active-listings"
          />
          <StatCard
            label="Total Reviews" value={reviews.total || 0}
            sub={`${reviews.approved || 0} approved`}
            color="text-warning" testId="kpi-total-reviews"
          />
          <StatCard
            label="Reported Reviews" value={reviews.reported || 0}
            color="text-danger" tone={toneIfPositive(reviews.reported)}
            testId="kpi-reported-reviews"
          />
        </div>
      </CollapsibleSection>

      {/* E. Search & Demand — collapsed by default (lower priority for daily admin work) */}
      <CollapsibleSection
        id="demand"
        title="Search & Demand"
        subtitle="What users are looking for"
        defaultOpen={false}
        testId="kpi-demand"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Top Category" value={demand?.top_category || '—'}
            sub={demand?.top_category_count
              ? `${demand.top_category_count} searches (7d)`
              : 'No searches yet'}
            color="text-text_main" testId="kpi-top-category"
          />
          <StatCard
            label="Top City" value={demand?.top_city || '—'}
            sub={demand?.top_city_count
              ? `${demand.top_city_count} active listings`
              : 'No listings yet'}
            color="text-text_main" testId="kpi-top-city"
          />
          <StatCard
            label="No-Result Searches" value={demand?.no_result_searches || 0}
            sub="Last 7 days"
            color={demand?.no_result_searches > 0 ? 'text-warning' : 'text-text_main'}
            testId="kpi-no-result-searches"
          />
          <StatCard
            label="Pending Reviews" value={reviews.pending || 0}
            sub="Awaiting moderation"
            color="text-warning" testId="kpi-pending-reviews"
          />
        </div>
      </CollapsibleSection>

      {/* F. Monetization — collapsed by default per user request (low-priority surface). */}
      <CollapsibleSection
        id="monetization"
        title="Monetization"
        subtitle="Revenue-driving surfaces"
        defaultOpen={false}
        testId="kpi-monetization"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Featured Listings" value={listings.featured || 0}
            color="text-accent" testId="kpi-featured"
          />
          <StatCard
            label="Boosted Listings" value={listings.boosted || 0}
            sub="Paid boosts"
            color="text-text_main" testId="kpi-boosted"
          />
          <StatCard
            label="Revenue (MGA)" value="—"
            sub="Coming soon"
            color="text-text_muted" testId="kpi-revenue"
          />
          <StatCard
            label="Total Messages" value={messages.total || 0}
            color="text-text_main" testId="kpi-total-messages"
          />
        </div>
      </CollapsibleSection>
    </div>
  );
};

/* ─── Listings ─── */
/* ─── Reviews ─── */
const REVIEW_FILTERS = [
  { key: 'pending', label: 'Pending', color: 'bg-warning-soft text-warning' },
  { key: 'approved', label: 'Approved', color: 'bg-success-soft text-success' },
  { key: 'rejected', label: 'Rejected', color: 'bg-danger-soft text-danger' },
];

const ReviewsSection = ({ reviews, filter, onFilterChange, onModerate, onDelete, renderStars, pagination, page, onPageChange, pageSize, onPageSizeChange, pageSizeOptions }) => (
  <div>
    <h2 className="text-xl font-bold mb-4">Reviews</h2>
    <div className="flex gap-2 mb-4" data-testid="review-filters">
      {REVIEW_FILTERS.map(f => (
        <button key={f.key} onClick={() => onFilterChange(f.key)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f.key ? f.color + ' ring-2 ring-offset-1 ring-primary' : 'bg-white border border-border text-text_muted hover:bg-surface'}`} data-testid={`filter-${f.key}`}>{f.label}</button>
      ))}
    </div>
    <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="admin-reviews-table">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold text-sm">Rating</th>
              <th className="text-left p-4 font-semibold text-sm">Comment</th>
              <th className="text-left p-4 font-semibold text-sm">Reviewer</th>
              <th className="text-left p-4 font-semibold text-sm">Seller</th>
              <th className="text-left p-4 font-semibold text-sm">Date</th>
              <th className="text-left p-4 font-semibold text-sm">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {reviews.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-text_muted">No {filter} reviews</td></tr>
            ) : reviews.map((r) => (
              <tr key={r.review_id} data-testid={`admin-review-${r.review_id}`}>
                <td className="p-4"><div className="flex gap-0.5">{renderStars(r.rating)}</div></td>
                <td className="p-4 max-w-xs text-sm truncate">{r.comment || <span className="italic text-text_muted">No comment</span>}</td>
                <td className="p-4 text-sm">{r.reviewer_name}<br/><span className="text-xs text-text_muted">{r.reviewer_email}</span></td>
                <td className="p-4 text-sm"><Link to={`/users/${r.seller_id}`} className="hover:text-primary">{r.seller_name}</Link></td>
                <td className="p-4 text-xs text-text_muted">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <div className="flex gap-1.5">
                    {r.status !== 'approved' && <Button size="sm" variant="success" onClick={() => onModerate(r.review_id, 'approved')} className="h-8 px-2" data-testid="approve-review-btn"><CheckCircle size={16} /></Button>}
                    {r.status !== 'rejected' && <Button size="sm" variant="destructive" onClick={() => onModerate(r.review_id, 'rejected')} className="h-8 px-2" data-testid="reject-review-btn"><XCircle size={16} /></Button>}
                    <ConfirmDelete onConfirm={() => onDelete(r.review_id)} title="Delete Review?" description="This review will be permanently removed." testId="delete-review-btn" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <AdminPagination
      total={pagination?.total ?? 0}
      page={pagination?.page ?? page ?? 1}
      pageSize={pagination?.page_size ?? pageSize ?? 10}
      totalPages={pagination?.total_pages ?? 0}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pageSizeOptions={pageSizeOptions}
      testIdPrefix="review"
    />
  </div>
);

/* ─── Users ─── */
const DEFAULT_ROLE_FALLBACK = [
  { role_key: 'user', name: 'User' },
  { role_key: 'moderator', name: 'Moderator' },
  { role_key: 'admin', name: 'Admin' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'risk', label: 'Risk Queue' },
  { value: 'all', label: 'All' },
];

const FLAG_REASONS = [
  { value: 'fraud', label: 'Fraud' },
  { value: 'scam', label: 'Scam' },
  { value: 'spam', label: 'Spam' },
  { value: 'bot', label: 'Bot' },
];

const FLAG_BADGE_CLASSES = {
  fraud: 'bg-danger-soft text-danger border-danger',
  scam: 'bg-danger-soft text-danger border-danger',
  spam: 'bg-warning-soft text-warning border-warning',
  bot: 'bg-warning-soft text-warning border-warning',
};

const statusFor = (u) => {
  if (u.deleted || u.status === 'deleted') return 'deleted';
  if (u.suspended || u.status === 'suspended') return 'suspended';
  if (u.status === 'inactive') return 'inactive';
  return 'active';
};

const STATUS_BADGE_VARIANT = {
  active: { variant: 'default', label: 'Active' },
  inactive: { variant: 'secondary', label: 'Inactive' },
  suspended: { variant: 'destructive', label: 'Suspended' },
  deleted: { variant: 'outline', label: 'Deleted' },
};

// Inline activity chip shown next to each user's name in the admin table.
// Tiered palette: 0 = neutral, 1–2 = gray, 3–9 = blue, 10+ = amber accent.
const ListingCountChip = ({ count }) => {
  const n = typeof count === 'number' ? count : 0;
  let tone = 'bg-surface text-text_muted border-border';
  if (n >= 10) tone = 'bg-warning-soft text-warning border-warning';
  else if (n >= 3) tone = 'bg-info-soft text-info border-info';
  else if (n >= 1) tone = 'bg-surface_muted text-text_main border-border';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap ${tone}`}
      data-testid="user-listing-count-chip"
      title={`${n} listings`}
    >
      {n} {n === 1 ? 'listing' : 'listings'}
    </span>
  );
};

// Risk score chip — only renders when score ≥ 55 (the review-queue threshold).
// 55..79 → review queue (warning tone). 80..100 → high-risk + listings hidden
// (danger tone). Tooltip lists the active signals.
const RiskScoreChip = ({ score, signals, hidden }) => {
  const n = typeof score === 'number' ? score : 0;
  if (n < 55) return null;
  const isHigh = n >= 80;
  const tone = isHigh
    ? 'bg-danger-soft text-danger border-danger'
    : 'bg-warning-soft text-warning border-warning';
  const label = isHigh ? `Risk ${n} • hidden` : `Risk ${n}`;
  const sigList = Array.isArray(signals) && signals.length ? signals.join(', ') : 'no signals';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${tone}`}
      data-testid="user-risk-chip"
      title={`Score ${n}/100 — ${sigList}${hidden ? ' — listings auto-hidden' : ''}`}
    >
      {label}
    </span>
  );
};

const UsersSection = ({
  users,
  availableRoles,
  search,
  onSearchChange,
  onSearch,
  statusFilter,
  onStatusFilterChange,
  includeDeleted,
  onIncludeDeletedChange,
  sort,
  onSortChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  pagination,
  onChangeRole,
  onSuspend,
  onDelete,
  onResetPassword,
  onFlag,
  onUnflag,
  onClearRisk,
  t,
}) => {
  const roleOptions = availableRoles?.length ? availableRoles : DEFAULT_ROLE_FALLBACK;
  const total = pagination?.total ?? users.length;
  const totalPages = pagination?.total_pages ?? 0;
  const currentPage = pagination?.page ?? page ?? 1;
  const effectivePageSize = pagination?.page_size ?? pageSize ?? 10;
  return (
  <div>
    <h2 className="text-xl font-bold mb-4">Users ({total})</h2>
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="flex gap-2" data-testid="user-search-form">
        <Input value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder={t('admin.searchUsers')} className="max-w-sm border border-border" data-testid="user-search-input" />
        <Button type="submit" className="bg-primary hover:bg-primary-hover text-white" data-testid="user-search-btn">Search</Button>
      </form>
      <div className="flex items-center gap-2 ml-auto">
        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger className="w-40 h-9" data-testid="user-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} data-testid={`user-status-filter-${opt.value}`}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort || 'default'} onValueChange={onSortChange}>
          <SelectTrigger className="w-56 h-9" data-testid="user-sort-filter" aria-label="Sort by">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default" data-testid="user-sort-default">Sort by: Default</SelectItem>
            <SelectItem value="name_asc" data-testid="user-sort-name-asc">Name A → Z</SelectItem>
            <SelectItem value="name_desc" data-testid="user-sort-name-desc">Name Z → A</SelectItem>
            <SelectItem value="most_active" data-testid="user-sort-most-active">Most recently active</SelectItem>
            <SelectItem value="most_listings" data-testid="user-sort-most-listings">Most listings</SelectItem>
          </SelectContent>
        </Select>
        {statusFilter !== 'deleted' && (
          <label className="flex items-center gap-1.5 text-xs text-text_muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => onIncludeDeletedChange(e.target.checked)}
              className="rounded border-border"
              data-testid="user-include-deleted"
            />
            Include deleted
          </label>
        )}
      </div>
    </div>
    <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="admin-users-table">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold text-sm">Name</th>
              <th className="text-left p-4 font-semibold text-sm">Email</th>
              <th className="text-left p-4 font-semibold text-sm">Role</th>
              <th className="text-left p-4 font-semibold text-sm">Status</th>
              <th className="text-left p-4 font-semibold text-sm">Last Active</th>
              <th className="text-left p-4 font-semibold text-sm">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-text_muted">No users found</td></tr>
            ) : users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-${u.user_id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/users/${u.user_id}`} className="font-medium hover:text-primary text-sm" data-testid="admin-user-name">{u.name || 'Unknown'}</Link>
                    <ListingCountChip count={u.listing_count} />
                  </div>
                </td>
                <td className="p-4 text-sm text-text_muted">{u.email}</td>
                <td className="p-4">
                  <Select value={u.role || 'user'} onValueChange={(val) => onChangeRole(u.user_id, val)}>
                    <SelectTrigger className="w-40 h-8 text-xs" data-testid="role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((r) => (
                        <SelectItem key={r.role_key} value={r.role_key}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  {(() => {
                    const s = statusFor(u);
                    const cfg = STATUS_BADGE_VARIANT[s];
                    const flag = u.flag_reason && u.flag_reason !== 'none' ? u.flag_reason : null;
                    return (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={cfg.variant} data-testid="user-status-badge">{cfg.label}</Badge>
                        {flag && (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border capitalize ${FLAG_BADGE_CLASSES[flag] || 'bg-danger-soft text-danger border-danger'}`}
                            data-testid="user-flag-badge"
                            title={`Flagged: ${flag}`}
                          >
                            <Flag size={10} weight="fill" /> {flag}
                          </span>
                        )}
                        <RiskScoreChip
                          score={u.risk_score}
                          signals={u.risk_signals}
                          hidden={u.risk_listings_hidden}
                        />
                      </div>
                    );
                  })()}
                </td>
                <td className="p-4 text-xs text-text_muted">{u.last_active ? formatDistanceToNow(new Date(u.last_active), { addSuffix: true }) : '-'}</td>
                <td className="p-4">
                  <div className="flex flex-col gap-1.5 min-w-[140px]" data-testid="user-actions-cell">
                    {/* Row 1: primary actions (Suspend / Lock / Delete) */}
                    <div className="flex items-center gap-1.5" data-testid="user-actions-primary">
                      {u.suspended ? (
                        <Button size="sm" variant="success" onClick={() => onSuspend(u.user_id, false)} className="h-8 w-8 p-0" data-testid="reactivate-user-btn" title="Reactivate"><ShieldCheck size={16} /></Button>
                      ) : (
                        <ConfirmAction onConfirm={() => onSuspend(u.user_id, true)} title="Suspend User?" description={`Suspend ${u.name || u.email}? Their listings will be hidden.`} testId="suspend-user-btn" variant="destructive" icon={<UserCircle size={16} />} />
                      )}
                      <Button size="sm" variant="outline" onClick={() => onResetPassword(u.user_id)} className="h-8 w-8 p-0" data-testid="reset-pw-btn" title="Reset Password"><Lock size={16} /></Button>
                      <ConfirmDelete onConfirm={() => onDelete(u.user_id)} title="Delete User?" description={`Permanently delete ${u.name || u.email}? This cannot be undone.`} testId="delete-user-btn" />
                    </div>
                    {/* Row 2: flag dropdown / unflag — full-width of the cell */}
                    <div className="w-full" data-testid="user-actions-secondary">
                      {u.flag_reason && u.flag_reason !== 'none' ? (
                        <Button size="sm" variant="outline" onClick={() => onUnflag(u.user_id)} className="h-8 w-full text-xs text-primary" data-testid="unflag-user-btn" title="Clear flag">
                          <ShieldCheck size={14} className="mr-1" /> Unflag
                        </Button>
                      ) : (
                        <Select value="" onValueChange={(v) => v && onFlag(u.user_id, v)}>
                          <SelectTrigger className="h-8 w-full text-xs border-warning text-warning hover:bg-warning-soft" data-testid="flag-user-trigger">
                            <span className="inline-flex items-center gap-1">
                              <Flag size={12} weight="fill" /> Flag
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {FLAG_REASONS.map((r) => (
                              <SelectItem key={r.value} value={r.value} data-testid={`flag-as-${r.value}`}>
                                Mark as {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    {/* Row 3 (conditional): Clear-risk action when score ≥ 55 */}
                    {typeof u.risk_score === 'number' && u.risk_score >= 55 && (
                      <div className="w-full" data-testid="user-actions-risk">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onClearRisk(u.user_id)}
                          className="h-8 w-full text-xs border-danger text-danger hover:bg-danger-soft"
                          data-testid="clear-risk-btn"
                          title="Clear risk score and restore listings"
                        >
                          <ShieldCheck size={14} className="mr-1" /> Clear risk
                        </Button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <AdminPagination
      total={total}
      page={currentPage}
      pageSize={effectivePageSize}
      totalPages={totalPages}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pageSizeOptions={pageSizeOptions}
      testIdPrefix="user"
    />
  </div>
  );
};

/* ─── Audit Log ─── */
const AUDIT_CATEGORIES = [
  { value: 'all', label: 'All actions' },
  { value: 'user', label: 'User actions' },
  { value: 'listing', label: 'Listing actions' },
  { value: 'report', label: 'Report actions' },
];

const AuditSection = ({
  logs,
  pagination,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions,
  category,
  onCategoryChange,
  actorSearch,
  onActorSearchChange,
  onActorSearchSubmit,
  onActorSearchClear,
}) => (
  <div data-testid="audit-section">
    <h2 className="text-xl font-bold mb-4">Audit Log</h2>
    <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="audit-filters">
      <Select value={category || 'all'} onValueChange={onCategoryChange}>
        <SelectTrigger className="w-44 h-9" data-testid="audit-category-filter" aria-label="Filter by action category">
          <SelectValue placeholder="Action category" />
        </SelectTrigger>
        <SelectContent>
          {AUDIT_CATEGORIES.map((c) => (
            <SelectItem key={c.value} value={c.value} data-testid={`audit-category-${c.value}`}>{c.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <form
        onSubmit={(e) => { e.preventDefault(); onActorSearchSubmit?.(); }}
        className="flex gap-2 flex-1 max-w-md"
        data-testid="audit-actor-search-form"
      >
        <Input
          value={actorSearch || ''}
          onChange={(e) => onActorSearchChange?.(e.target.value)}
          placeholder="Search by user (name or email)"
          className="border border-border h-9"
          data-testid="audit-actor-search-input"
        />
        <Button type="submit" className="bg-primary hover:bg-primary-hover text-white h-9" data-testid="audit-actor-search-btn">
          Search
        </Button>
        {actorSearch ? (
          <Button type="button" variant="outline" onClick={onActorSearchClear} className="h-9" data-testid="audit-actor-search-clear">
            Clear
          </Button>
        ) : null}
      </form>
    </div>
    <div className="bg-white border border-border rounded-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface border-b border-border">
            <tr>
              <th className="text-left p-4 font-semibold text-sm">When</th>
              <th className="text-left p-4 font-semibold text-sm">Actor</th>
              <th className="text-left p-4 font-semibold text-sm">Action</th>
              <th className="text-left p-4 font-semibold text-sm">Target</th>
              <th className="text-left p-4 font-semibold text-sm">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {logs.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-text_muted">No actions logged yet</td></tr>
            ) : logs.map((log, i) => (
              <tr key={log.log_id || i}>
                <td className="p-4 text-xs text-text_muted whitespace-nowrap">{log.created_at ? formatDistanceToNow(new Date(log.created_at), { addSuffix: true }) : '-'}</td>
                <td className="p-4 text-sm font-medium">{log.actor_name}</td>
                <td className="p-4"><Badge variant="secondary" className="text-xs">{log.action}</Badge></td>
                <td className="p-4 text-xs text-text_muted">{log.target_type}: {log.target_id?.slice(0, 20)}</td>
                <td className="p-4 text-xs text-text_muted">{log.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    <AdminPagination
      total={pagination?.total ?? 0}
      page={pagination?.page ?? page ?? 1}
      pageSize={pagination?.page_size ?? pageSize ?? 10}
      totalPages={pagination?.total_pages ?? 0}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pageSizeOptions={pageSizeOptions}
      testIdPrefix="audit"
    />
  </div>
);

/* ─── Promotions Management ─── */
const PromotionsSection = ({ promotions, filter, setFilter, onAction }) => {
  const tierLabels = { featured: 'Featured Listing', top_category: 'Top of Category', homepage: 'Homepage Highlight' };
  const tierColors = { featured: 'bg-warning-soft text-warning', top_category: 'bg-info-soft text-info', homepage: 'bg-success-soft text-success' };

  return (
    <div data-testid="promotions-section">
      <h2 className="text-xl font-bold mb-1">Promotions</h2>
      <p className="text-sm text-text_muted mb-4">Review and manage listing promotion requests.</p>

      <div className="flex gap-2 mb-4">
        {['pending', 'active', 'rejected'].map(s => (
          <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm"
            onClick={() => setFilter(s)}
            className={`text-xs rounded-full ${filter === s ? 'bg-primary text-white' : ''}`}
            data-testid={`promo-filter-${s}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {promotions.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-8 text-center text-text_muted" data-testid="no-promotions">
          No {filter} promotions
        </div>
      ) : (
        <div className="space-y-3" data-testid="promotions-list">
          {promotions.map(p => (
            <div key={p.promo_id} className="bg-white border border-border rounded-lg p-4 flex items-center gap-4" data-testid={`promo-${p.promo_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{p.listing_title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`text-[10px] ${tierColors[p.tier] || ''}`}>{tierLabels[p.tier] || p.tier}</Badge>
                  <span className="text-xs text-text_muted">{p.created_at ? formatDistanceToNow(new Date(p.created_at), { addSuffix: true }) : ''}</span>
                </div>
              </div>
              {filter === 'pending' && (
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="success" onClick={() => onAction(p.promo_id, 'approve')} className="text-xs gap-1" data-testid="approve-promo-btn">
                    <CheckCircle size={14} /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onAction(p.promo_id, 'reject')} className="text-danger border-danger hover:bg-danger-soft text-xs gap-1" data-testid="reject-promo-btn">
                    <XCircle size={14} /> Reject
                  </Button>
                </div>
              )}
              {filter !== 'pending' && (
                <Badge variant={p.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                  {p.status}
                </Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Platform Settings ─── */
const SettingsSection = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin';
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);
  const [platformTimezone, setPlatformTimezone] = useState(DEFAULT_PLATFORM_TZ);
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const timezoneOptions = getStandardTimezones();

  useEffect(() => {
    // Timezone is readable/editable by anyone who reached this tab (already
    // gated by the `manage_platform_config` permission at the sidebar level);
    // maintenance mode stays a super-admin-only concern below. Single GET,
    // both states derived from the same response.
    axios.get(`${API}/admin/platform-config`, { withCredentials: true })
      .then(({ data }) => {
        setPlatformTimezone(data.platform_timezone || DEFAULT_PLATFORM_TZ);
        if (isSuperAdmin) setMaintenanceMode(!!data.maintenance_mode);
      })
      .catch(() => {})
      .finally(() => setMaintenanceLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimezoneChange = async (tz) => {
    setTimezoneSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/platform-config`,
        { platform_timezone: tz },
        { withCredentials: true },
      );
      setPlatformTimezone(data.platform_timezone);
      setCachedPlatformTimezone(data.platform_timezone);
      toast.success('Fuseau horaire de la plateforme mis à jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la mise à jour du fuseau horaire');
    } finally {
      setTimezoneSaving(false);
    }
  };

  const toggleMaintenanceMode = async (checked) => {
    if (checked && !window.confirm('Turn ON Maintenance Mode? Visitors will be unable to access the site until you turn this off again.')) {
      return;
    }
    setMaintenanceSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/platform-config`,
        { maintenance_mode: checked },
        { withCredentials: true },
      );
      setMaintenanceMode(!!data.maintenance_mode);
      toast.success(checked ? 'Maintenance mode is now ON — the site is closed to visitors' : 'Maintenance mode is now OFF — the site is live again');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update maintenance mode');
    } finally {
      setMaintenanceSaving(false);
    }
  };

  return (
    <div data-testid="settings-section">
      <h2 className="text-xl font-bold mb-1">Platform Settings</h2>
      <p className="text-sm text-text_muted mb-6">Manage maintenance mode, platform timezone, and social media links.</p>

      {isSuperAdmin && !maintenanceLoading && (
        <div className="bg-white border border-border rounded-lg p-6 mb-6 max-w-xl" data-testid="maintenance-mode-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center mt-0.5 border ${maintenanceMode ? 'bg-danger-soft border-danger' : 'bg-warning-soft border-warning'}`}>
                <Wrench size={18} weight="bold" className={maintenanceMode ? 'text-danger' : 'text-warning'} />
              </div>
              <div>
                <p className="text-sm font-semibold text-text_main">Maintenance Mode</p>
                <p className="text-xs text-text_muted mt-0.5 max-w-sm">
                  When ON, visitors can't access the site — every page shows the "Site en maintenance" page (editable in Pages) instead. Only admins bypass this. Super-admin only.
                </p>
                {maintenanceMode && (
                  <p className="text-xs text-danger font-semibold mt-1.5" data-testid="maintenance-mode-active-warning">
                    Currently ON — visitors cannot access the site.
                  </p>
                )}
              </div>
            </div>
            <Switch
              checked={maintenanceMode}
              onCheckedChange={toggleMaintenanceMode}
              disabled={maintenanceSaving}
              data-testid="maintenance-mode-toggle"
              className="mt-1"
            />
          </div>
        </div>
      )}

      <div className="bg-white border border-border rounded-lg p-6 mb-6 max-w-xl" data-testid="platform-timezone-card">
        <p className="text-sm font-semibold text-text_main mb-1">Fuseau horaire de la plateforme</p>
        <p className="text-xs text-text_muted mb-3 max-w-sm">
          Utilisé pour afficher les dates/heures dans l'Admin Panel (logs, queue email, campagnes, journal d'audit) et pour planifier l'envoi des campagnes email en heure locale.
        </p>
        <Select value={platformTimezone} onValueChange={handleTimezoneChange} disabled={timezoneSaving}>
          <SelectTrigger className="max-w-sm" data-testid="platform-timezone-select">
            <SelectValue placeholder="Sélectionner un fuseau horaire" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {timezoneOptions.map((tz) => (
              <SelectItem key={tz} value={tz}>{tz}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-border rounded-lg p-6">
        <SocialLinksManagement />
      </div>
    </div>
  );
};

/* ─── Field Management Section ─── */
const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
];

const FieldManagementSection = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);

  const selectedCat = categories.find(c => c.value === selectedCategory);
  const subcatOptions = selectedCat?.subcategories || [];

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data)).catch(() => {});
  }, []);

  // Reset subcategory when category changes
  const handleCategoryChange = (val) => {
    setSelectedCategory(val);
    setSelectedSubcategory('');
    setFields([]);
    setEditingIdx(null);
  };

  // Fetch fields when both category AND subcategory are selected
  useEffect(() => {
    if (!selectedCategory || !selectedSubcategory) { setFields([]); return; }
    setLoadingFields(true);
    axios.get(`${API}/admin/category-fields/${selectedCategory}/${selectedSubcategory}`, { withCredentials: true })
      .then(res => setFields((res.data.fields || []).map((f, i) => ({ ...f, _uid: f._uid || `f_loaded_${i}_${f.key || 'k'}` }))))
      .catch(() => setFields([]))
      .finally(() => setLoadingFields(false));
  }, [selectedCategory, selectedSubcategory]);

  const addField = () => {
    const newField = { _uid: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, key: '', label: '', type: 'text', options: [], placeholder: '', required: false, order: fields.length + 1, validation: {} };
    setFields([...fields, newField]);
    setEditingIdx(fields.length);
  };

  const updateField = (idx, updates) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, ...updates } : f));
  };

  const removeField = (idx) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
    setEditingIdx(null);
  };

  const moveField = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const updated = [...fields];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((f, i) => { f.order = i + 1; });
    setFields(updated);
    if (editingIdx === idx) setEditingIdx(newIdx);
    else if (editingIdx === newIdx) setEditingIdx(idx);
  };

  const saveFields = async () => {
    if (!selectedSubcategory) { toast.error('Please select a subcategory first'); return; }
    for (const f of fields) {
      if (!f.key.trim()) { toast.error(`Field key cannot be empty`); return; }
      if (!f.label.trim()) { toast.error(`Field label cannot be empty for key: ${f.key}`); return; }
      if ((f.type === 'dropdown' || f.type === 'multi_select') && (!f.options || f.options.length === 0)) {
        toast.error(`Field "${f.label}" requires at least one option`); return;
      }
    }
    setSaving(true);
    try {
      const ordered = fields.map((f, i) => ({ ...f, order: i + 1 }));
      await axios.put(`${API}/admin/category-fields/${selectedCategory}/${selectedSubcategory}`, { fields: ordered }, { withCredentials: true });
      toast.success('Fields saved successfully');
      setEditingIdx(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save fields');
    } finally { setSaving(false); }
  };

  const generateKey = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  return (
    <div className="space-y-6" data-testid="field-management-section">
      <h2 className="text-lg font-medium text-text_strong tracking-wide">Custom Fields</h2>
      <p className="text-sm text-text_muted">Define custom fields per subcategory. These fields appear on the Create/Edit listing form for end users.</p>

      {/* Category + Subcategory Selectors */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-text_main mb-2 block">Category</Label>
            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
              <SelectTrigger data-testid="field-category-select">
                <SelectValue placeholder="Choose a category..." />
              </SelectTrigger>
              <SelectContent>
                {categories.filter(c => c.value !== 'other').map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-text_main mb-2 block">Subcategory</Label>
            <Select value={selectedSubcategory} onValueChange={(v) => { setSelectedSubcategory(v); setEditingIdx(null); }} disabled={!selectedCategory}>
              <SelectTrigger data-testid="field-subcategory-select" className={!selectedCategory ? 'opacity-50' : ''}>
                <SelectValue placeholder={selectedCategory ? 'Choose a subcategory...' : 'Select category first'} />
              </SelectTrigger>
              <SelectContent>
                {subcatOptions.map((sub) => (
                  <SelectItem key={sub.value} value={sub.value}>{sub.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {selectedCategory && !selectedSubcategory && (
          <p className="text-sm text-warning mt-3" data-testid="select-subcategory-hint">Please select a subcategory to manage custom fields</p>
        )}
      </div>

      {/* Field Editor — only when both category AND subcategory selected */}
      {selectedCategory && selectedSubcategory && (
        <div className="bg-white border border-border rounded-xl p-5 space-y-4" data-testid="field-editor">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-text_main">
              Fields for: <span className="text-primary">{selectedCat?.name}</span> / <span className="text-primary">{subcatOptions.find(s => s.value === selectedSubcategory)?.name}</span>
              <span className="text-text_muted text-sm ml-2">({fields.length} fields)</span>
            </h3>
            <div className="flex gap-2">
              <Button size="sm" variant="primary" onClick={addField} className="gap-1.5" data-testid="add-field-btn">
                <Plus size={16} weight="bold" /> Add Field
              </Button>
              <Button size="sm" variant="success" onClick={saveFields} disabled={saving} data-testid="save-fields-btn">
                {saving ? 'Saving...' : 'Save All'}
              </Button>
            </div>
          </div>

          {loadingFields ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : fields.length === 0 ? (
            <div className="text-center py-8 text-text_muted" data-testid="no-fields">
              <p className="mb-2">No custom fields defined for this category yet.</p>
              <Button size="sm" variant="outline" onClick={addField} className="gap-1.5"><Plus size={16} /> Add your first field</Button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="field-list">
              {fields.map((field, idx) => (
                <div key={field._uid || `field-${idx}`} className={`border rounded-lg transition-all ${editingIdx === idx ? 'border-primary bg-primary/[0.02] p-4' : 'border-border p-3'}`} data-testid={`field-item-${idx}`}>
                  {/* Collapsed View */}
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-0.5">
                      <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="text-text_muted hover:text-text_main disabled:opacity-30" data-testid={`move-up-${idx}`}><ArrowUp size={14} /></button>
                      <button onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1} className="text-text_muted hover:text-text_main disabled:opacity-30" data-testid={`move-down-${idx}`}><ArrowDown size={14} /></button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-surface px-2 py-0.5 rounded font-mono text-text_muted">{field.type}</span>
                        <span className="font-medium text-sm text-text_main truncate">{field.label || '(untitled)'}</span>
                        {field.required && <span className="text-xs text-danger">required</span>}
                        <span className="text-xs text-text_muted font-mono">key: {field.key || '—'}</span>
                      </div>
                    </div>
                    <button onClick={() => setEditingIdx(editingIdx === idx ? null : idx)} className="text-text_muted hover:text-primary" data-testid={`edit-field-${idx}`}>
                      <PencilSimple size={18} />
                    </button>
                    <button onClick={() => removeField(idx)} className="text-text_muted hover:text-danger" data-testid={`delete-field-${idx}`}>
                      <Trash size={18} />
                    </button>
                  </div>

                  {/* Expanded Edit Form */}
                  {editingIdx === idx && (
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={`field-edit-form-${idx}`}>
                      <div>
                        <Label className="text-xs text-text_muted mb-1 block">Label *</Label>
                        <Input value={field.label} onChange={(e) => { updateField(idx, { label: e.target.value, key: field.key || generateKey(e.target.value) }); }} placeholder="e.g. Brand" className="border border-border text-sm" data-testid={`field-label-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-text_muted mb-1 block">Key (slug) *</Label>
                        <Input value={field.key} onChange={(e) => updateField(idx, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} placeholder="e.g. brand" className="border border-border text-sm font-mono" data-testid={`field-key-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-text_muted mb-1 block">Type *</Label>
                        <Select value={field.type} onValueChange={(v) => updateField(idx, { type: v, options: (v === 'dropdown' || v === 'multi_select') ? (field.options || []) : [] })}>
                          <SelectTrigger data-testid={`field-type-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-text_muted mb-1 block">Placeholder</Label>
                        <Input value={field.placeholder || ''} onChange={(e) => updateField(idx, { placeholder: e.target.value })} placeholder="e.g. Enter brand" className="border border-border text-sm" data-testid={`field-placeholder-${idx}`} />
                      </div>
                      <div className="flex items-end gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={field.required} onChange={(e) => updateField(idx, { required: e.target.checked })} className="rounded border-border" data-testid={`field-required-${idx}`} />
                          <span className="text-sm text-text_main">Required</span>
                        </label>
                      </div>
                      {(field.type === 'dropdown' || field.type === 'multi_select') && (
                        <div className="col-span-2 md:col-span-3">
                          <Label className="text-xs text-text_muted mb-1 block">Options (one per line) *</Label>
                          <textarea
                            value={(field.options || []).join('\n')}
                            onChange={(e) => updateField(idx, { options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                            rows={4}
                            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                            placeholder="Option 1&#10;Option 2&#10;Option 3"
                            data-testid={`field-options-${idx}`}
                          />
                          <p className="text-xs text-text_muted mt-1">{(field.options || []).length} options defined</p>
                        </div>
                      )}
                      {field.type === 'number' && (
                        <div className="col-span-2 md:col-span-3 grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-text_muted mb-1 block">Min value</Label>
                            <Input type="number" value={field.validation?.min ?? ''} onChange={(e) => updateField(idx, { validation: { ...field.validation, min: e.target.value ? Number(e.target.value) : null } })} className="border border-border text-sm" data-testid={`field-min-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-xs text-text_muted mb-1 block">Max value</Label>
                            <Input type="number" value={field.validation?.max ?? ''} onChange={(e) => updateField(idx, { validation: { ...field.validation, max: e.target.value ? Number(e.target.value) : null } })} className="border border-border text-sm" data-testid={`field-max-${idx}`} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Database Export/Import Section ─── */
const DatabaseSection = ({ stats, onExport, onImport, exporting, importing }) => {
  const collectionLabels = {
    users: 'Users', listings: 'Listings', reviews: 'Reviews', messages: 'Messages',
    favorites: 'Favorites', newsletter: 'Newsletter', promotions: 'Promotions',
    notifications: 'Notifications', saved_searches: 'Saved Searches',
    review_reports: 'Review Reports', platform_settings: 'Platform Settings',
    listing_views: 'Listing Views', files: 'Files', admin_audit_log: 'Audit Log'
  };

  return (
    <div className="space-y-6" data-testid="database-section">
      <h2 className="text-lg font-medium text-text_strong tracking-wide">Database Management</h2>

      {/* Export Card */}
      <div className="bg-white border border-border rounded-xl p-6" data-testid="export-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-success-soft p-2.5 rounded-lg"><DownloadSimple size={22} className="text-success" weight="bold" /></div>
          <div>
            <h3 className="font-medium text-text_main">Export Database</h3>
            <p className="text-sm text-text_muted">Download all data as JSON</p>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-5">
            {Object.entries(stats).filter(([k]) => k !== 'total').map(([key, count]) => (
              <div key={key} className="bg-surface rounded-lg px-3 py-2">
                <p className="text-xs text-text_muted">{collectionLabels[key] || key}</p>
                <p className="text-base font-semibold text-text_main">{count}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-4">
          <Button onClick={onExport} disabled={exporting} variant="success" className="gap-2" data-testid="export-btn">
            <DownloadSimple size={18} weight="bold" />
            {exporting ? 'Exporting...' : 'Export All Data'}
          </Button>
          {stats && <span className="text-sm text-text_muted">{stats.total} total documents</span>}
        </div>
      </div>

      {/* Import Card */}
      <div className="bg-white border border-border rounded-xl p-6" data-testid="import-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-warning-soft p-2.5 rounded-lg"><UploadSimple size={22} className="text-warning" weight="bold" /></div>
          <div>
            <h3 className="font-medium text-text_main">Import Database</h3>
            <p className="text-sm text-text_muted">Restore from a JSON export file (upsert mode — existing data is updated, new data is added)</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${importing ? 'border-border bg-surface_muted text-text_muted' : 'border-warning bg-warning-soft text-warning hover:border-warning hover:bg-warning-soft'}`} data-testid="import-btn">
            <UploadSimple size={18} weight="bold" />
            {importing ? 'Importing...' : 'Choose JSON file'}
            <input type="file" accept=".json" onChange={onImport} disabled={importing} className="hidden" />
          </label>
        </div>

        <div className="mt-4 p-3 bg-surface rounded-lg border border-border">
          <p className="text-xs text-text_muted leading-relaxed">
            <strong>How it works:</strong> Import uses upsert mode. Records with matching IDs are updated, new records are inserted. No data is deleted during import. Use export first to create a backup before importing.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ─── Reports ─── */
const REASON_LABELS = {
  fraud_scam: 'Fraud / Scam',
  inappropriate_content: 'Inappropriate Content',
  wrong_category: 'Wrong Category',
  duplicate: 'Duplicate',
  spam: 'Spam',
  other: 'Other',
};

const ReportsSection = ({ reports, filter, setFilter, onAction, pagination, page, onPageChange, pageSize, onPageSizeChange, pageSizeOptions }) => (
  <div data-testid="reports-section">
    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
      <h2 className="text-xl font-bold">Listing Reports</h2>
      <div className="flex gap-2">
        {['pending', 'dismissed', 'actioned', 'all'].map(s => (
          <Button key={s} size="sm" variant={filter === s ? 'default' : 'outline'} onClick={() => setFilter(s)} data-testid={`report-filter-${s}`} className={filter === s ? 'bg-primary text-white' : ''}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>
    </div>
    {reports.length === 0 ? (
      <div className="bg-white border border-border rounded-lg p-8 text-center text-text_muted" data-testid="no-reports">
        No reports found
      </div>
    ) : (
      <div className="space-y-3">
        {reports.map(r => (
          <div key={r.report_id} className="bg-white border border-border rounded-lg p-4" data-testid={`report-${r.report_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={r.status === 'pending' ? 'bg-warning-soft text-warning' : r.status === 'actioned' ? 'bg-danger-soft text-danger' : 'bg-surface_muted text-text_main'}>
                    {r.status}
                  </Badge>
                  <span className="text-xs font-medium text-text_muted bg-surface px-2 py-0.5 rounded">
                    {REASON_LABELS[r.reason] || r.reason}
                  </span>
                </div>
                <p className="font-semibold text-sm text-text_main mb-1">
                  Listing: <Link to={`/listings/${r.listing_id}`} className="text-primary hover:underline">{r.listing_title || r.listing_id}</Link>
                </p>
                <p className="text-xs text-text_muted">
                  Reported by: <span className="font-medium text-text_main">{r.reporter_name}</span>
                  {' · '}Owner: <span className="font-medium text-text_main">{r.listing_owner_name}</span>
                  {r.created_at && <> · {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</>}
                </p>
                {r.comment && (
                  <p className="text-sm text-text_main mt-2 bg-surface rounded px-3 py-2 italic">"{r.comment}"</p>
                )}
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => onAction(r.report_id, 'dismiss')} className="text-text_main hover:bg-surface_muted" data-testid={`dismiss-report-${r.report_id}`}>
                    Dismiss
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => onAction(r.report_id, 'remove_listing')} data-testid={`remove-listing-${r.report_id}`}>
                    <Trash size={14} className="mr-1" /> Remove Listing
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
    <AdminPagination
      total={pagination?.total ?? 0}
      page={pagination?.page ?? page ?? 1}
      pageSize={pagination?.page_size ?? pageSize ?? 10}
      totalPages={pagination?.total_pages ?? 0}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pageSizeOptions={pageSizeOptions}
      testIdPrefix="report"
    />
  </div>
);

/* ─── Confirmation Dialogs ─── */
const ConfirmDelete = ({ onConfirm, title, description, testId }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-danger hover:bg-danger-soft border-danger" data-testid={testId} title="Delete"><Trash size={16} /></Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-danger hover:bg-danger">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const ConfirmAction = ({ onConfirm, title, description, testId, variant = 'destructive', icon }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button size="sm" variant={variant} className="h-8 w-8 p-0" data-testid={testId} title={title}>{icon}</Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-danger hover:bg-danger">Confirm</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default AdminPage;
